/**
 * bnc-stack-save.js
 * PDP tiered "stack & save" card as a Horizon custom element.
 *
 * Radiogroup keyboard + click selection and derived-string recompute
 * (pure DOM, no cart mutation). Subscribes to Horizon's document
 * 'cart:update' to recount the in-cart bundle quantity and refresh tier
 * active/unlocked state + footer summary + CTA.
 *
 * Adapted from Dopamiles' simplified stack-save: the progress bar,
 * per-tier total, and variant-driven "From $X" total were removed, so
 * this element no longer tracks unit price or subscribes to variant
 * updates — tier savings are fixed per-unit dollar amounts.
 *
 * Eligibility note: the cart:update payload carries no per-item
 * collection handle, so the count reads /cart.js and sums quantities
 * excluding the shipping-protection add-on. Accurate bundle-eligible
 * filtering is server-side (SSR Liquid) + the native discount at
 * checkout. This widget motivates; it does not enforce.
 */
(function () {
  'use strict';

  var CHECK_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="m5 12 5 5L20 7"/></svg>';

  function getMoneyFormat() {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      var fmt = (window.cart && window.cart.money_format) || window.Shopify.money_format;
      if (fmt) return fmt;
    }
    return null;
  }

  function formatMoney(cents) {
    var fmt = getMoneyFormat();
    if (fmt && window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try { return window.Shopify.formatMoney(cents, fmt); } catch (e) {}
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' })
        .format(cents / 100)
        .replace(/\.00$/, '');
    } catch (e) {
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    }
  }

  // Sum cart quantities, excluding the shipping-protection variant (an add-on,
  // not a bundle tee). Reads /cart.js for per-line data the cart:update event
  // doesn't carry.
  function fetchCartCount(spVariantId) {
    if (typeof fetch !== 'function') return Promise.resolve(null);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var spId = spVariantId ? String(spVariantId) : null;
    return fetch(root + 'cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (c) {
        if (!c || !Array.isArray(c.items)) return null;
        var n = 0;
        c.items.forEach(function (it) {
          if (spId && String(it.variant_id) === spId) return;
          n += it.quantity;
        });
        return n;
      })
      .catch(function () { return null; });
  }

  class BncStackSave extends HTMLElement {
    connectedCallback() {
      if (this.__wired) return;
      this.__wired = true;

      this.state = {
        picked: parseInt(this.dataset.defaultPicked, 10) || 3,
        inCart: parseInt(this.dataset.inCart, 10) || 0,
        maxQty: parseInt(this.dataset.maxQty, 10) || 5,
        collectionUrl: this.dataset.collectionUrl || '/collections/bundle-eligible',
      };
      // Shipping-protection variant is an add-on, not a bundle tee — exclude
      // it from the in-cart count so ticking SP doesn't inflate the tier.
      this.spVariantId = this.dataset.spVariantId || '';

      this.tiers = this.collectTiers();
      if (!this.tiers.length) return;

      this.els = {
        summaryQty: this.querySelector('[data-bnc-stack-summary-qty]'),
        summarySave: this.querySelector('[data-bnc-stack-summary-save]'),
        footer: this.querySelector('.bnc-stack-foot'),
      };

      this.bindRadioGroup();

      this.onCartUpdate = this.handleCartUpdate.bind(this);
      document.addEventListener('cart:update', this.onCartUpdate);

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this.onCartUpdate);
    }

    collectTiers() {
      var btns = Array.prototype.slice.call(this.querySelectorAll('[role="radio"][data-tier-qty]'));
      return btns.map(function (btn) {
        return {
          qty: parseInt(btn.dataset.tierQty, 10),
          off: parseInt(btn.dataset.tierOff, 10),
          flag: btn.dataset.tierFlag || '',
          btn: btn,
        };
      });
    }

    findTarget(picked) {
      for (var i = 0; i < this.tiers.length; i++) if (this.tiers[i].qty === picked) return this.tiers[i];
      return this.tiers[Math.floor(this.tiers.length / 2)];
    }

    render() {
      var state = this.state;
      var els = this.els;
      var target = this.findTarget(state.picked);
      var needed = Math.max(0, target.qty - state.inCart);
      var savingsCents = target.qty * target.off * 100;

      this.tiers.forEach(function (t) {
        var active = t.qty === state.picked;
        var unlocked = state.inCart >= t.qty && !active;
        t.btn.classList.toggle('active', active);
        t.btn.classList.toggle('unlocked', unlocked);
        t.btn.setAttribute('aria-checked', String(active));
        t.btn.setAttribute('tabindex', active ? '0' : '-1');
        BncStackSave.syncTierBadge(t.btn, active, t.flag);
      });

      if (els.summaryQty) els.summaryQty.textContent = String(target.qty);
      if (els.summarySave) els.summarySave.textContent = 'Save ' + formatMoney(savingsCents);

      this.syncCta(needed);
    }

    static syncTierBadge(btn, active, flagText) {
      var existingFlag = btn.querySelector('.bnc-stack-tier-flag');
      var existingCheck = btn.querySelector('.bnc-stack-tier-check');

      // Flag persists whenever the tier carries flag text (top-center),
      // independent of selection. Check is the active-only marker (top-right).
      if (flagText && !existingFlag) {
        var flag = document.createElement('span');
        flag.className = 'bnc-stack-tier-flag';
        flag.textContent = flagText;
        btn.insertBefore(flag, btn.firstChild);
      } else if (!flagText && existingFlag) {
        existingFlag.remove();
      }

      if (active) {
        if (!existingCheck) {
          var check = document.createElement('span');
          check.className = 'bnc-stack-tier-check';
          check.setAttribute('aria-hidden', 'true');
          check.innerHTML = CHECK_SVG;
          btn.insertBefore(check, btn.firstChild);
        }
      } else if (existingCheck) {
        existingCheck.remove();
      }
    }

    syncCta(needed) {
      var footer = this.els.footer;
      if (!footer) return;
      var current = footer.querySelector('[data-bnc-stack-cta]');
      if (!current) return;
      var wasFocused = document.activeElement === current;

      if (needed === 0) {
        if (current.tagName !== 'BUTTON') {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'bnc-stack-cta';
          btn.setAttribute('data-bnc-stack-cta', '');
          btn.disabled = true;
          btn.setAttribute('aria-label', 'Bundle tier is active');
          btn.textContent = 'Tier active';
          current.replaceWith(btn);
        } else {
          current.disabled = true;
          current.setAttribute('aria-label', 'Bundle tier is active');
          current.textContent = 'Tier active';
        }
      } else {
        var label = 'Shop ' + needed + ' more ' + (needed === 1 ? 'tee' : 'tees') + ' →';
        if (current.tagName !== 'A') {
          var a = document.createElement('a');
          a.className = 'bnc-stack-cta';
          a.setAttribute('data-bnc-stack-cta', '');
          a.href = this.state.collectionUrl;
          a.textContent = label;
          current.replaceWith(a);
          if (wasFocused) a.focus();
        } else {
          current.href = this.state.collectionUrl;
          current.textContent = label;
        }
      }
    }

    bindRadioGroup() {
      var self = this;
      this.tiers.forEach(function (t, idx) {
        t.btn.addEventListener('click', function () {
          if (self.state.picked === t.qty) return;
          self.state.picked = t.qty;
          self.render();
          t.btn.focus();
        });
        t.btn.addEventListener('keydown', function (e) {
          var next = -1;
          switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
              next = (idx + 1) % self.tiers.length;
              break;
            case 'ArrowLeft':
            case 'ArrowUp':
              next = (idx - 1 + self.tiers.length) % self.tiers.length;
              break;
            case 'Home':
              next = 0;
              break;
            case 'End':
              next = self.tiers.length - 1;
              break;
            case ' ':
            case 'Enter':
              e.preventDefault();
              if (self.state.picked !== t.qty) {
                self.state.picked = t.qty;
                self.render();
              }
              return;
            default:
              return;
          }
          e.preventDefault();
          self.state.picked = self.tiers[next].qty;
          self.render();
          self.tiers[next].btn.focus();
        });
      });
    }

    handleCartUpdate() {
      var self = this;
      // The event only carries a total item_count (which includes the SP
      // add-on), so read /cart.js and sum quantities excluding SP.
      fetchCartCount(this.spVariantId).then(function (count) {
        if (typeof count !== 'number') return;
        self.state.inCart = count;
        self.render();
      });
    }
  }

  if (!customElements.get('bnc-stack-save')) {
    customElements.define('bnc-stack-save', BncStackSave);
  }
})();
